import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DateTextField from '../../components/DateTextField'
import {
  type LookupItem,
  type SaleContract,
  type SaleContractPayload,
  useCreateSaleContractMutation,
  useCopyDraftMutation,
  useGetSaleContractQuery,
  useLazySuggestContractNoQuery,
  useLazyCheckContractSaleExistsQuery,
  useUpdateSaleContractMutation,
  useGetLookupContractTypesQuery,
  useGetLookupFixationCallsQuery,
  useGetLookupPriceUnitsQuery,
  useGetLookupFuturesMonthsQuery,
  useGetLookupDeliveryTermsQuery,
  useGetLookupPaymentWeightTermsQuery,
  useGetLookupConditionsQuery,
  useGetLookupCertificationsQuery,
  useLazyGetLookupClientsQuery,
  useLazyGetLookupConsigneesQuery,
  useLazyGetLookupPackagesQuery,
  useLazyGetLookupQualitiesQuery,
  useLazyGetLookupBrokersQuery,
  useLazyGetLookupTradersQuery,
} from '../../store/api/saleContractApi'
import { usePagePermission } from '../../hooks/usePagePermission'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'

/* ------------------------------------------------------------------ */
/*  Form model                                                         */
/* ------------------------------------------------------------------ */

interface SaleContractFormModel {
  contractNo: string
  buyerRef: string
  contractDate: string
  clientCode: string
  clientName: string
  consigneeCode: string
  consigneeName: string
  quantity: string
  packagingCode: string
  packagingName: string
  bags: string
  qualityCode: string
  certificationCode: string
  contractType: string
  fixationCall: string
  price: string
  priceUnit: string
  ptbfDiff: string
  vatApplicable: boolean | null
  vatRate: string
  futuresMonth: string
  fxRate: string
  deliveryTerm: string
  warehouse: string
  shipmentDateFrom: string
  shipmentDateTo: string
  deliveryDateFrom: string
  deliveryDateTo: string
  isShipDeli: number
  pss: string
  paymentWeightTerm: string
  franchise: string
  paymentTerms: string
  conditionsCode: string
  note: string
  freight: string
  logisticsCost: string
  insuranceCost: string
  weightingCost: string
  financeCost: string
  bankCost: string
  description: string
  isIncludedRoyal: boolean
  royalFee: string
  brokerCode: string
  brokerFirm: string
  brokerRef: string
  commission: string
  traderCode: string
  traderName: string
  sentContract: boolean
  sentPaper: boolean
  receivedContract: boolean
  receivedPaper: boolean
}

const emptyForm = (): SaleContractFormModel => ({
  contractNo: '',
  buyerRef: '',
  contractDate: new Date().toISOString().slice(0, 10),
  clientCode: '',
  clientName: '',
  consigneeCode: '',
  consigneeName: '',
  quantity: '',
  packagingCode: '',
  packagingName: '',
  bags: '',
  qualityCode: '',
  certificationCode: '',
  contractType: '',
  fixationCall: '',
  price: '',
  priceUnit: '',
  ptbfDiff: '',
  vatApplicable: false,
  vatRate: '',
  futuresMonth: '',
  fxRate: '',
  deliveryTerm: '',
  warehouse: '',
  shipmentDateFrom: '',
  shipmentDateTo: '',
  deliveryDateFrom: '',
  deliveryDateTo: '',
  isShipDeli: 1,
  pss: '',
  paymentWeightTerm: '',
  franchise: '',
  paymentTerms: '',
  conditionsCode: '',
  note: '',
  freight: '',
  logisticsCost: '',
  insuranceCost: '',
  weightingCost: '',
  financeCost: '',
  bankCost: '',
  description: '',
  isIncludedRoyal: false,
  royalFee: '',
  brokerCode: '',
  brokerFirm: '',
  brokerRef: '',
  commission: '',
  traderCode: '',
  traderName: '',
  sentContract: false,
  sentPaper: false,
  receivedContract: false,
  receivedPaper: false,
})

const toNum = (v: string): number | null => {
  if (!v) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

const contractToForm = (c: SaleContract | SaleContractPayload): SaleContractFormModel => ({
  contractNo: c.contractNo,
  buyerRef: c.buyerRef ?? '',
  contractDate: c.contractDate,
  clientCode: c.clientCode,
  clientName: ('clientName' in c ? c.clientName : null) ?? '',
  consigneeCode: c.consigneeCode ?? '',
  consigneeName: ('consigneeName' in c ? c.consigneeName : null) ?? '',
  quantity: c.quantity != null ? String(c.quantity) : '',
  packagingCode: c.packagingCode ?? '',
  packagingName: ('packaging' in c ? c.packaging : null) ?? '',
  bags: c.bags != null ? String(c.bags) : '',
  qualityCode: c.qualityCode ?? '',
  certificationCode: c.certificationCode ?? '',
  contractType: c.contractType,
  fixationCall: c.fixationCall ?? '',
  price: c.price != null ? String(c.price) : '',
  priceUnit: c.priceUnit ?? '',
  ptbfDiff: c.ptbfDiff != null ? String(c.ptbfDiff) : '',
  /* Access btnEdit_Click: `applicable_vat = IIf(IsNull(vat_rate), 0, -1)` —
   * checkbox VAT derive từ vat_rate, không lưu trữ riêng. Stored
   * `vatApplicable` chỉ là fallback cho row legacy chưa có vat_rate. */
  vatApplicable: c.vatRate != null ? true : (c.vatApplicable ?? null),
  vatRate: c.vatRate != null ? String(c.vatRate) : '',
  futuresMonth: c.futuresMonth ?? '',
  fxRate: c.fxRate != null ? String(c.fxRate) : '',
  deliveryTerm: c.deliveryTerm ?? '',
  warehouse: c.warehouse ?? '',
  shipmentDateFrom: c.shipmentDateFrom ?? '',
  shipmentDateTo: c.shipmentDateTo ?? '',
  deliveryDateFrom: c.deliveryDateFrom ?? '',
  deliveryDateTo: c.deliveryDateTo ?? '',
  isShipDeli: c.isShipDeli ?? 0,
  pss: c.pss ?? '',
  paymentWeightTerm: c.paymentWeightTerm ?? '',
  franchise: c.franchise != null ? String(c.franchise) : '',
  paymentTerms: c.paymentTerms ?? '',
  conditionsCode: c.conditionsCode ?? '',
  note: c.note ?? '',
  freight: c.freight != null ? String(c.freight) : '',
  logisticsCost: c.logisticsCost != null ? String(c.logisticsCost) : '',
  insuranceCost: c.insuranceCost != null ? String(c.insuranceCost) : '',
  weightingCost: c.weightingCost != null ? String(c.weightingCost) : '',
  financeCost: c.financeCost != null ? String(c.financeCost) : '',
  bankCost: c.bankCost != null ? String(c.bankCost) : '',
  description: c.description ?? '',
  isIncludedRoyal: c.isIncludedRoyal ?? false,
  royalFee: c.royalFee != null ? String(c.royalFee) : '',
  brokerCode: c.brokerCode ?? '',
  brokerFirm: ('brokerFirm' in c ? c.brokerFirm : null) ?? '',
  brokerRef: c.brokerRef ?? '',
  commission: c.commission != null ? String(c.commission) : '',
  traderCode: c.traderCode ?? '',
  traderName: ('traderName' in c ? c.traderName : null) ?? '',
  sentContract: c.sentContract,
  sentPaper: c.sentPaper,
  receivedContract: c.receivedContract,
  receivedPaper: c.receivedPaper,
})

const formToPayload = (f: SaleContractFormModel): SaleContractPayload => ({
  contractNo: f.contractNo,
  buyerRef: f.buyerRef || null,
  contractDate: f.contractDate,
  clientCode: f.clientCode,
  consigneeCode: f.consigneeCode || null,
  quantity: toNum(f.quantity) ?? 0,
  packagingCode: f.packagingCode || null,
  bags: toNum(f.bags),
  qualityCode: f.qualityCode || null,
  certificationCode: f.certificationCode || null,
  contractType: f.contractType,
  fixationCall: f.fixationCall || null,
  price: toNum(f.price),
  priceUnit: f.priceUnit || null,
  ptbfDiff: toNum(f.ptbfDiff),
  vatApplicable: f.vatApplicable,
  vatRate: toNum(f.vatRate),
  futuresMonth: f.futuresMonth || null,
  fxRate: toNum(f.fxRate),
  deliveryTerm: f.deliveryTerm || null,
  warehouse: f.warehouse.trim() ? f.warehouse : '',
  shipmentDateFrom: f.shipmentDateFrom || null,
  shipmentDateTo: f.shipmentDateTo || null,
  deliveryDateFrom: f.deliveryDateFrom || null,
  deliveryDateTo: f.deliveryDateTo || null,
  isShipDeli: f.isShipDeli,
  pss: f.pss || null,
  paymentWeightTerm: f.paymentWeightTerm || null,
  franchise: toNum(f.franchise),
  paymentTerms: f.paymentTerms || null,
  conditionsCode: f.conditionsCode || null,
  note: f.note || null,
  freight: toNum(f.freight),
  logisticsCost: toNum(f.logisticsCost),
  insuranceCost: toNum(f.insuranceCost),
  weightingCost: toNum(f.weightingCost),
  financeCost: toNum(f.financeCost),
  bankCost: toNum(f.bankCost),
  description: f.description || null,
  isIncludedRoyal: f.isIncludedRoyal,
  royalFee: toNum(f.royalFee),
  brokerCode: f.brokerCode || null,
  brokerRef: f.brokerRef || null,
  commission: toNum(f.commission),
  traderCode: f.traderCode || null,
  sentContract: f.sentContract,
  sentPaper: f.sentPaper,
  receivedContract: f.receivedContract,
  receivedPaper: f.receivedPaper,
})

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface ContractFormDialogProps {
  open: boolean
  onClose: () => void
  mode: 'new' | 'copy' | 'edit'
  contractId?: number
  contractNo?: string
  initialDraft?: SaleContractPayload | null
  controllerRef?: MutableRefObject<SaleContractFormController | null>
}

export interface SaleContractFormController {
  setField: (field: string, value: unknown) => string
  save: () => Promise<string>
  cancel: () => string
  getState: () => string
}

/* ------------------------------------------------------------------ */
/*  Field-type style helpers                                           */
/* ------------------------------------------------------------------ */

const fmtNumStr = (s: string): string => {
  if (!s) return ''
  const neg = s.startsWith('-')
  const abs = neg ? s.slice(1) : s
  const [intPart, ...rest] = abs.split('.')
  const intFormatted = intPart ? Number(intPart).toLocaleString('en-US') : '0'
  const result = rest.length > 0 ? `${intFormatted}.${rest.join('.')}` : intFormatted
  return neg ? `-${result}` : result
}

const numSx = { '& input': { textAlign: 'right' as const } } as const

// Skill `/convert-form` readonly standard: neutral gray thay vì `action.hover`
// (overlay phớt hồng trên theme TLC cream).
const readonlySx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
    cursor: 'default',
  },
} as const

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to save contract. Please try again.'
  if (!('data' in error)) return 'Failed to save contract. Please try again.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save contract. Please try again.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string' && msg.trim()) return msg
    }
    return JSON.stringify(first)
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)

  return 'Failed to save contract. Please try again.'
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ContractFormDialog({ open, onClose, mode, contractId, contractNo, initialDraft, controllerRef }: ContractFormDialogProps) {
  // Defense-in-depth: list-view đã gate Add/Edit nhưng vẫn check ở dialog
  // phòng deep-link / route bypass. View-only role → Save disabled.
  const { canEdit } = usePagePermission()
  const [form, setForm] = useState<SaleContractFormModel>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  /* ----- RTK Query hooks ----- */
  /* Pattern Futures Order: CHỈ dùng lazy hook (không subscribe regular query)
   * để tránh cache shared gây race giữa effect 2 (apply suggestedNo lần đầu)
   * và handler blur (trigger refetch). Lazy trigger trong openNew + blur. */
  const [triggerSuggestContractNo, suggestState] = useLazySuggestContractNoQuery()
  const [triggerCheckContractSale] = useLazyCheckContractSaleExistsQuery()

  /** Pre-save check state (skill access-suggest). */
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  // Race guard: blur + Save click cùng tick → ref sync (xem skill anti-pattern #5).
  const precheckBusyRef = useRef(false)
  const { data: editData, isFetching: editFetching } = useGetSaleContractQuery(contractNo!, { skip: mode !== 'edit' || !contractNo || !open })
  const [copyDraft, { data: copyData, isLoading: isCopying }] = useCopyDraftMutation()
  const [createContract, { isLoading: isCreating }] = useCreateSaleContractMutation()
  const [updateContract, { isLoading: isUpdating }] = useUpdateSaleContractMutation()

  /* Lookup selects (loaded once) */
  const { data: contractTypes } = useGetLookupContractTypesQuery(undefined, { skip: !open })
  const { data: fixationCalls } = useGetLookupFixationCallsQuery(undefined, { skip: !open })
  const { data: priceUnits } = useGetLookupPriceUnitsQuery(undefined, { skip: !open })
  const { data: futuresMonths } = useGetLookupFuturesMonthsQuery(undefined, { skip: !open })
  const { data: deliveryTerms } = useGetLookupDeliveryTermsQuery(undefined, { skip: !open })
  const { data: paymentWeightTerms } = useGetLookupPaymentWeightTermsQuery(undefined, { skip: !open })
  const { data: conditions } = useGetLookupConditionsQuery(undefined, { skip: !open })
  const { data: certifications } = useGetLookupCertificationsQuery(undefined, { skip: !open })

  /* Lazy autocomplete queries */
  const [fetchClients, { data: clientOpts, isFetching: clientsLoading }] = useLazyGetLookupClientsQuery()
  const [fetchConsignees, { data: consigneeOpts, isFetching: consigneesLoading }] = useLazyGetLookupConsigneesQuery()
  const [fetchPackages, { data: packageOpts, isFetching: packagesLoading }] = useLazyGetLookupPackagesQuery()
  const [fetchQualities, { data: qualityOpts, isFetching: qualitiesLoading }] = useLazyGetLookupQualitiesQuery()
  const [fetchBrokers, { data: brokerOpts, isFetching: brokersLoading }] = useLazyGetLookupBrokersQuery()
  const [fetchTraders, { data: traderOpts, isFetching: tradersLoading }] = useLazyGetLookupTradersQuery()

  /* ---- Cached packaging meta for bags derivation ---- */
  const packagingMeta = useRef<Record<string, number>>({})

  /* ---- Initialize form on open ---- */
  useEffect(() => {
    if (!open) return
    if (mode === 'new') {
      if (initialDraft) {
        setForm(contractToForm(initialDraft))
        setValidationError(null)
        setFieldErrors({})
        return
      }
      // KHÔNG đưa suggestedNo vào hydrate (skill anti #4 biến thể): khi
      // blur trigger refetch suggest, cache update → effect re-fire →
      // reset form. Apply suggested qua effect riêng dưới.
      setForm(emptyForm())
    } else if (mode === 'edit' && editData) {
      setForm(contractToForm(editData))
    } else if (mode === 'copy' && contractId) {
      copyDraft(contractId)
    }
    setValidationError(null)
    setFieldErrors({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, editData, initialDraft])

  // Pattern Futures Order: lazy trigger suggest sau khi dialog mở (mode=new).
  // Force refetch (preferCacheValue=false) để mỗi lần mở đều lấy mã mới nhất
  // từ BE. Apply vào form nếu contractNo còn rỗng. Cancel khi unmount/đổi mode.
  useEffect(() => {
    if (!open || mode !== 'new') return
    let cancelled = false
    void triggerSuggestContractNo(undefined, false)
      .unwrap()
      .then((res) => {
        if (cancelled) return
        const newVal = (res.contractNo || '').trim()
        if (!newVal) return
        setForm((prev) => (prev.contractNo.trim() ? prev : { ...prev, contractNo: newVal }))
      })
      .catch(() => {
        /* keep dialog open even if suggestion service is briefly unavailable */
      })
    return () => {
      cancelled = true
    }
  }, [open, mode, triggerSuggestContractNo])

  useEffect(() => {
    if (mode === 'copy' && copyData?.draft) {
      const f = contractToForm(copyData.draft)
      setForm({
        ...f,
        brokerFirm: copyData.brokerFirm || f.brokerFirm,
        traderName: copyData.traderName || f.traderName,
      })
    }
  }, [mode, copyData])

  /* Pre-load autocomplete options on dialog open */
  useEffect(() => {
    if (!open) return
    fetchClients()
    fetchConsignees()
    fetchPackages()
    fetchQualities()
    fetchBrokers()
    fetchTraders()
  }, [open, fetchClients, fetchConsignees, fetchPackages, fetchQualities, fetchBrokers, fetchTraders])

  /* ---- Helpers ---- */
  const clearFieldError = useCallback((key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  const set = useCallback(
    <K extends keyof SaleContractFormModel>(key: K, value: SaleContractFormModel[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }))
      clearFieldError(key as string)
    },
    [clearFieldError],
  )

  const setNum = useCallback(
    (key: keyof SaleContractFormModel, raw: string) => {
      setForm((prev) => ({ ...prev, [key]: raw.replace(/,/g, '') }))
      clearFieldError(key as string)
    },
    [clearFieldError],
  )

  const debouncedSearch = useCallback(
    (fetcher: (q: string) => void, q: string) => {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => fetcher(q), 300)
    },
    [],
  )

  const sdCur = form.isShipDeli ?? 1
  const showShipment = sdCur === 0 || sdCur === 1
  const showDelivery = sdCur === 0 || sdCur === 2

  /* ---- Pre-save check (skill access-suggest) ---- */
  // Reset state khi dialog mở.
  useEffect(() => {
    if (!open) return
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
  }, [open, mode, contractId, contractNo])

  // Auto-dismiss alert khi user sửa contractNo khác mã suggest.
  useEffect(() => {
    if (regenNotice && form.contractNo.trim() !== regenNotice.to) {
      setRegenNotice(null)
    }
  }, [form.contractNo, regenNotice])

  const handleContractNoBlur = async () => {
    if (mode === 'edit') return
    const val = form.contractNo.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheckContractSale({ contractNo: val }).unwrap()
      if (!check.exists) return
      // preferCacheValue=false → force network refetch (skill anti-pattern #7).
      const next = await triggerSuggestContractNo(undefined, false).unwrap()
      const newVal = (next.contractNo || '').trim()
      if (newVal && newVal !== val) {
        setForm((prev) => ({ ...prev, contractNo: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE sẽ raise lúc Save nếu thật sự trùng — không poison UX.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  /* ---- Lookup option lists ---- */
  const clientItems = useMemo(() => clientOpts?.items ?? [], [clientOpts])
  const consigneeItems = useMemo(() => consigneeOpts?.items ?? [], [consigneeOpts])
  const packageItems = useMemo(() => packageOpts?.items ?? [], [packageOpts])
  const qualityItems = useMemo(() => qualityOpts?.items ?? [], [qualityOpts])
  const brokerItems = useMemo(() => brokerOpts?.items ?? [], [brokerOpts])
  const traderItems = useMemo(() => traderOpts?.items ?? [], [traderOpts])

  /* Populate packagingMeta từ lookup response. Copy/Edit mode mở dialog
   * với packagingCode có sẵn nhưng `packagingMeta.current` empty (user chưa
   * click Autocomplete), → bags derivation skip. Sync ngay khi packageOpts
   * load để có nw_per_bag_kg cho mọi code đã thấy. */
  useEffect(() => {
    for (const item of packageItems) {
      const nw = (item.meta as { nw_per_bag_kg?: number | string } | undefined)?.nw_per_bag_kg
      if (nw != null && item.value) {
        packagingMeta.current[item.value] = Number(nw)
      }
    }
  }, [packageItems])

  /* Bags derivation — re-run khi packageItems load (Copy/Edit mode: dialog
   * mở với packagingCode sẵn nhưng packageOpts arrive async sau, ref vừa
   * được populate ở useEffect phía trên). */
  useEffect(() => {
    const qty = toNum(form.quantity)
    const weight = packagingMeta.current[form.packagingCode]
    if (qty != null && weight != null && weight > 0) {
      setForm((prev) => ({ ...prev, bags: String(Math.round(qty / weight)) }))
    }
  }, [form.quantity, form.packagingCode, packageItems])

  const validateLikeAccess = (): { message: string | null; errors: Record<string, string> } => {
    const errors: Record<string, string> = {}
    const messages: string[] = []
    const addError = (field: string, msg: string) => {
      if (!errors[field]) {
        errors[field] = msg
        messages.push(msg)
      }
    }

    if (!form.contractDate) addError('contractDate', 'Please choose Contract date!')
    if (!form.contractNo.trim()) addError('contractNo', 'Please type Contract no!')
    if (!form.clientCode.trim()) addError('clientCode', 'Please choose Client!')
    if (form.quantity === '') addError('quantity', 'Please type Quantity!')
    if (!form.packagingCode.trim()) addError('packagingCode', 'Please choose Package!')
    if (form.bags === '') addError('bags', 'Please type No of bags!')
    if (!form.qualityCode.trim()) addError('qualityCode', 'Please choose Quality!')
    if (!form.certificationCode.trim()) addError('certificationCode', 'Please choose Certification!')
    if (!form.contractType.trim()) addError('contractType', 'Please choose Contract Type!')

    const isOutright = form.contractType.trim().toLowerCase() === 'outright'
    const isDifferential = form.contractType.trim().toLowerCase() === 'differential'
    if (isOutright && form.price === '') addError('price', 'Please type Price!')
    if (isDifferential && form.ptbfDiff === '') addError('ptbfDiff', 'Please type PTBF diffs!')
    if (form.ptbfDiff !== '' && form.price !== '') {
      addError('price', 'Choose only Price or PTBF diffs!')
      addError('ptbfDiff', 'Choose only Price or PTBF diffs!')
    }
    if (!form.priceUnit.trim()) addError('priceUnit', 'Please choose Price unit!')
    if (!form.deliveryTerm.trim()) addError('deliveryTerm', 'Please choose Delivery Term!')

    // Shipment month LUÔN bắt buộc cho mọi HĐ Sales — lưu để traffic căn ngày
    // ship hàng, độc lập với checkbox print (checkbox chỉ chọn hiện Shipment
    // hay Delivery trên form print, không gate việc nhập/lưu Shipment).
    if (!form.shipmentDateFrom) addError('shipmentDateFrom', 'Please choose Shipment From!')
    if (!form.shipmentDateTo) addError('shipmentDateTo', 'Please choose Shipment To!')
    // From ≤ To — chuỗi YYYY-MM-DD so sánh lexicographic = chronological.
    if (
      form.shipmentDateFrom
      && form.shipmentDateTo
      && form.shipmentDateFrom > form.shipmentDateTo
    ) {
      const msg =
        "Shipment 'From' date cannot be later than the 'To' date."
      addError('shipmentDateFrom', msg)
      addError('shipmentDateTo', msg)
    }
    if (showDelivery) {
      if (!form.deliveryDateFrom) addError('deliveryDateFrom', 'Please choose Delivery From!')
      if (!form.deliveryDateTo) addError('deliveryDateTo', 'Please choose Delivery To!')
      if (
        form.deliveryDateFrom
        && form.deliveryDateTo
        && form.deliveryDateFrom > form.deliveryDateTo
      ) {
        const msg =
          "Delivery 'From' date cannot be later than the 'To' date."
        addError('deliveryDateFrom', msg)
        addError('deliveryDateTo', msg)
      }
    }
    if ((showShipment && showDelivery) || (!showShipment && !showDelivery)) {
      messages.push('Please tick to display either Shipment Month or Delivery Month!')
    }

    const term = form.deliveryTerm.trim().toUpperCase()
    if ((term.startsWith('CIF') || term.startsWith('CFR') || term.startsWith('CNF')) && form.freight === '') {
      addError('freight', 'Please type Freight!')
    }
    if (!form.traderCode.trim()) addError('traderCode', 'Please choose Trader In Charge!')
    if (form.vatApplicable && form.vatRate === '') addError('vatRate', 'Please input VAT Rate!')
    if (!form.vatApplicable && form.vatRate !== '') addError('vatRate', 'Please do not input VAT Rate!')

    return { message: messages[0] ?? null, errors }
  }

  /* ---- Save ---- */
  const handleSave = async () => {
    // Race guard (skill anti-pattern #5): blur + Save click cùng tick →
    // blur start handler set ref=true. Click Save tới đây phải bỏ qua,
    // đợi precheck resolve mới Save lần 2.
    if (precheckBusyRef.current) return
    const { message, errors } = validateLikeAccess()
    setFieldErrors(errors)
    if (message || Object.keys(errors).length > 0) {
      setValidationError(message ?? 'Please fix the highlighted fields.')
      return
    }
    setValidationError(null)
    const body = formToPayload(form)
    try {
      if (mode === 'edit' && contractId) {
        await updateContract({ id: contractId, body }).unwrap()
      } else {
        await createContract(body).unwrap()
      }
      onClose()
    } catch (e: unknown) {
      const errMsg = extractApiErrorMessage(e)
      // Access parity: BE raise "S contract is existed!" / "already exists" /
      // duplicate → FE tự suggest mã mới. Skill access-suggest anti #13.
      const isDup = mode !== 'edit' && /exist|duplicate/i.test(errMsg)
      if (isDup) {
        try {
          const next = await triggerSuggestContractNo(undefined, false).unwrap()
          const newVal = (next.contractNo || '').trim()
          const oldVal = form.contractNo.trim()
          if (newVal && newVal !== oldVal) {
            setForm((prev) => ({ ...prev, contractNo: newVal }))
            setRegenNotice({ from: oldVal, to: newVal })
            setValidationError(null)
            return
          }
        } catch {
          /* fallthrough → hiển thị error gốc */
        }
      }
      setValidationError(errMsg)
    }
  }

  const handleCancel = () => {
    clearTimeout(debounceRef.current)
    setForm(emptyForm())
    setValidationError(null)
    onClose()
  }

  const isBusy = isCreating || isUpdating || isCopying

  // Edit/Copy mở NGAY (open-immediately) + spinner trong dialog trong lúc
  // get-by-id (edit) / copy-draft (copy) chưa resolve. Form cần detail đầy đủ
  // từ public_local_s (royal_fee/is_included_royal/vat_rate/is_ship_deli…) —
  // không có sẵn trên list row nên phải fetch, không hydrate đồng bộ được.
  const contentLoading =
    open &&
    ((mode === 'edit' && !!contractNo && (editFetching || !editData)) ||
      (mode === 'copy' && (isCopying || !copyData?.draft)))

  const dialogTitle = 'Sales Contract - Sale'

  useEffect(() => {
    if (!controllerRef) return
    controllerRef.current = {
      setField: (field, value) => {
        if (!(field in form)) {
          return `Field "${field}" does not exist in the Sales Contract form.`
        }
        setForm((prev) => ({ ...prev, [field]: value } as SaleContractFormModel))
        return `Updated ${field}.`
      },
      save: async () => {
        await handleSave()
        return 'Sales Contract save executed.'
      },
      cancel: () => {
        handleCancel()
        return 'Sales Contract form closed.'
      },
      getState: () =>
        `Sale form open=${open}, mode=${mode}, contractNo=${form.contractNo || '(empty)'}, clientCode=${form.clientCode || '(empty)'}`,
    }
    return () => {
      controllerRef.current = null
    }
  }, [controllerRef, form, handleSave, handleCancel, mode, open])

  /* ---- Static Autocomplete (preloaded enum/lookup, no search) ---- */
  const renderStaticAutocomplete = (
    label: string,
    valueCode: string,
    options: LookupItem[],
    tabIndex: number,
    onSelect: (item: LookupItem | null) => void,
    errorKey?: string,
  ) => {
    const selectedOption: LookupItem | null = valueCode
      ? options.find((o) => o.value === valueCode) ?? { value: valueCode, label: valueCode }
      : null
    // Inject fallback vào options khi vắng → tắt MUI "value invalid / none of
    // options match" (skill /convert-form anti-pattern #46).
    const opts = selectedOption && !options.some((o) => o.value === selectedOption.value)
      ? [selectedOption, ...options]
      : options
    const errMsg = errorKey ? fieldErrors[errorKey] : undefined
    return (
      <Autocomplete
        size="small"
        options={opts}
        value={selectedOption}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        onChange={(_, v) => onSelect(v)}
        handleHomeEndKeys
        autoHighlight
        selectOnFocus
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={!!errMsg}
            helperText={errMsg}
            inputProps={{ ...params.inputProps, tabIndex }}
          />
        )}
      />
    )
  }

  /* ---- Autocomplete helper (list-only; rejects free-text) ---- */
  const renderAutocomplete = (
    label: string,
    valueCode: string,
    valueLabel: string,
    options: LookupItem[],
    loading: boolean,
    tabIndex: number,
    onSearch: (q: string) => void,
    onSelect: (item: LookupItem | null) => void,
    errorKey?: string,
    // codeOnly: ô input chỉ hiện CODE (dùng khi có field name readonly riêng,
    // vd Broker Code + Broker Name); dropdown vẫn "<code> — <name>".
    codeOnly = false,
  ) => {
    const selectedOption: LookupItem | null = valueCode
      ? options.find((o) => o.value === valueCode) ?? { value: valueCode, label: valueLabel || valueCode }
      : null
    // Server-search: value đã lưu (Edit/Copy) gần như KHÔNG có trong options →
    // inject vào options để tắt MUI warning (skill /convert-form anti-pattern #46).
    const opts = selectedOption && !options.some((o) => o.value === selectedOption.value)
      ? [selectedOption, ...options]
      : options
    const errMsg = errorKey ? fieldErrors[errorKey] : undefined
    return (
      <Autocomplete
        size="small"
        options={opts}
        loading={loading}
        value={selectedOption}
        getOptionLabel={(o) => (typeof o === 'string' ? o : (codeOnly ? o.value : o.label))}
        renderOption={codeOnly ? (liProps, o) => (
          <li {...liProps} key={o.value}>{o.label}</li>
        ) : undefined}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        onInputChange={(_, v, reason) => {
          if (reason === 'input') onSearch(v)
        }}
        onChange={(_, v) => onSelect(v)}
        filterOptions={(x) => x}
        clearOnBlur
        handleHomeEndKeys
        autoHighlight
        selectOnFocus
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={!!errMsg}
            helperText={errMsg}
            inputProps={{
              ...params.inputProps,
              tabIndex,
            }}
          />
        )}
      />
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(900px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          noWrap
          title={dialogTitle}
          sx={{
            ...TEXT_TRUNCATE,
            flex: '1 1 auto',
            fontWeight: 700,
            fontSize: '1rem',
            minWidth: 0,
          }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            Sales Contract -{' '}
          </Box>
          Sale
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={isBusy || !canEdit}
            sx={{
              py: 0.25,
              px: 1,
              minHeight: 28,
              fontSize: '0.75rem',
              lineHeight: 1.2,
              textTransform: 'none',
            }}
          >
            {isBusy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            sx={{
              py: 0.25,
              px: 1,
              minHeight: 28,
              fontSize: '0.75rem',
              lineHeight: 1.2,
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiInputBase-root': {
            minHeight: 32,
            fontSize: '0.8125rem',
          },
          '& .MuiInputBase-input': {
            py: 0.5,
            fontSize: '0.8125rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.8125rem',
            fontWeight: 500,
          },
          '& .MuiInputLabel-shrink': {
            fontWeight: 600,
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.8125rem',
            fontWeight: 500,
          },
          '& .MuiCheckbox-root': {
            p: 0.5,
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
          },
          '& .MuiTypography-caption': {
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          },
          '& .MuiTypography-body2': {
            fontSize: '0.8125rem',
          },
        }}
      >
        {regenNotice && (
          <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
            Contract No <b>{regenNotice.from}</b> was just created — suggested
            new code <b>{regenNotice.to}</b>.
          </Alert>
        )}
        {validationError && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {validationError}
          </Typography>
        )}

        {/* Two-column grid — Row/Column pairs per DocConvert §8.5.1a. */}
        {contentLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 8,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        ) : (
        <Box sx={{ maxWidth: 980, mx: 'auto', pb: 1.5 }}>
          <Grid container spacing={0.75}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                ID
              </Typography>
              <Typography variant="body2" sx={{ minHeight: 32, lineHeight: '32px', color: 'text.secondary' }}>
                {mode === 'edit' && contractId != null ? contractId : '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTextField
                label="Contract Date"
                size="small"
                fullWidth
                value={form.contractDate}
                onChange={(v) => set('contractDate', v)}
                nativeInputProps={{ tabIndex: 1 }}
                error={!!fieldErrors.contractDate}
                helperText={fieldErrors.contractDate}
                /* Access btnEdit_Click: contract_date.Enabled = False khi edit. */
                disabled={mode === 'edit'}
                sx={mode === 'edit' ? readonlySx : undefined}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Contract No"
                size="small"
                fullWidth
                value={form.contractNo}
                onChange={(e) => set('contractNo', e.target.value)}
                onBlur={() => void handleContractNoBlur()}
                InputProps={{ readOnly: mode === 'edit' }}
                inputProps={{ tabIndex: 2 }}
                sx={mode === 'edit' ? readonlySx : undefined}
                error={!!fieldErrors.contractNo}
                helperText={
                  fieldErrors.contractNo ??
                  (precheckBusy
                    ? 'Verifying contract code…'
                    : suggestState.isFetching
                      ? 'Suggesting next code…'
                      : undefined)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Buyer's Ref"
                size="small"
                fullWidth
                value={form.buyerRef}
                onChange={(e) => set('buyerRef', e.target.value)}
                inputProps={{ tabIndex: 3 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Client Name',
                form.clientCode,
                form.clientName,
                clientItems,
                clientsLoading,
                4,
                (q) => debouncedSearch((s) => fetchClients(s), q),
                (item) => {
                  set('clientCode', item?.value ?? '')
                  set('clientName', item?.label ?? '')
                },
                'clientCode',
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Client Code" size="small" fullWidth value={form.clientCode} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 5 }} sx={readonlySx} />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Consignee',
                form.consigneeCode,
                form.consigneeName,
                consigneeItems,
                consigneesLoading,
                6,
                (q) => debouncedSearch((s) => fetchConsignees(s), q),
                (item) => {
                  set('consigneeCode', item?.value ?? '')
                  set('consigneeName', item?.label ?? '')
                },
                'consigneeCode',
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Quantity (Kg)"
                size="small"
                fullWidth
                value={fmtNumStr(form.quantity)}
                onChange={(e) => setNum('quantity', e.target.value)}
                inputProps={{ tabIndex: 7, inputMode: 'decimal' }}
                sx={numSx}
                error={!!fieldErrors.quantity}
                helperText={fieldErrors.quantity}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Packaging',
                form.packagingCode,
                form.packagingName,
                packageItems,
                packagesLoading,
                8,
                (q) => debouncedSearch((s) => fetchPackages(s), q),
                (item) => {
                  set('packagingCode', item?.value ?? '')
                  set('packagingName', item?.label ?? '')
                  const nw = item?.meta?.nw_per_bag_kg
                  if (nw != null && item?.value) {
                    packagingMeta.current[item.value] = Number(nw)
                  }
                },
                'packagingCode',
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bags"
                size="small"
                fullWidth
                value={fmtNumStr(form.bags)}
                onChange={(e) => setNum('bags', e.target.value)}
                inputProps={{ tabIndex: 9, inputMode: 'decimal' }}
                sx={numSx}
                error={!!fieldErrors.bags}
                helperText={fieldErrors.bags}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Quality',
                form.qualityCode,
                form.qualityCode,
                qualityItems,
                qualitiesLoading,
                10,
                (q) => debouncedSearch((s) => fetchQualities(s), q),
                (item) => set('qualityCode', item?.value ?? ''),
                'qualityCode',
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderStaticAutocomplete(
                'Certification',
                form.certificationCode,
                certifications?.items ?? [],
                11,
                (item) => set('certificationCode', item?.value ?? ''),
                'certificationCode',
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              {(() => {
                const ctOpts = contractTypes?.items ?? []
                const ctSel: LookupItem | null = form.contractType
                  ? ctOpts.find((o) => o.value === form.contractType) ?? { value: form.contractType, label: form.contractType }
                  : null
                return (
                  <Autocomplete
                    size="small"
                    options={ctOpts}
                    value={ctSel}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    onChange={(_, item) => {
                      const val = item?.value ?? ''
                      // Không disable/xoá Price & PTBF Diff theo Contract Type —
                      // cho nhập tự do, chỉ cảnh báo qua validation khi Save nếu
                      // nhập sai (Outright thiếu Price / Differential thiếu PTBF /
                      // nhập cả hai).
                      setForm((prev) => ({ ...prev, contractType: val }))
                      clearFieldError('contractType')
                    }}
                    handleHomeEndKeys
                    autoHighlight
                    selectOnFocus
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Contract Type"
                        error={!!fieldErrors.contractType}
                        helperText={fieldErrors.contractType}
                        inputProps={{ ...params.inputProps, tabIndex: 12 }}
                      />
                    )}
                  />
                )
              })()}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderStaticAutocomplete(
                'Fixation Call',
                form.fixationCall,
                fixationCalls?.items ?? [],
                13,
                (item) => set('fixationCall', item?.value ?? ''),
                'fixationCall',
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Price"
                size="small"
                fullWidth
                value={fmtNumStr(form.price)}
                onChange={(e) => setNum('price', e.target.value)}
                inputProps={{ tabIndex: 14, inputMode: 'decimal' }}
                sx={numSx}
                error={!!fieldErrors.price}
                helperText={fieldErrors.price}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderStaticAutocomplete(
                'Price Unit',
                form.priceUnit,
                priceUnits?.items ?? [],
                15,
                (item) => set('priceUnit', item?.value ?? ''),
                'priceUnit',
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="PTBF Diffs"
                size="small"
                fullWidth
                value={fmtNumStr(form.ptbfDiff)}
                onChange={(e) => setNum('ptbfDiff', e.target.value)}
                inputProps={{ tabIndex: 16, inputMode: 'decimal' }}
                sx={numSx}
                error={!!fieldErrors.ptbfDiff}
                helperText={fieldErrors.ptbfDiff}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                sx={{ ml: 0, mt: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(form.vatApplicable)}
                    tabIndex={17}
                    onChange={(e) => {
                      const checked = e.target.checked
                      set('vatApplicable', checked)
                      if (!checked) set('vatRate', '')
                    }}
                  />
                }
                label="Applicable VAT?"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="VAT Rate"
                size="small"
                fullWidth
                value={fmtNumStr(form.vatRate)}
                onChange={(e) => setNum('vatRate', e.target.value)}
                inputProps={{ tabIndex: 18, inputMode: 'decimal' }}
                sx={numSx}
                error={!!fieldErrors.vatRate}
                helperText={fieldErrors.vatRate}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderStaticAutocomplete(
                'Futures Month',
                form.futuresMonth,
                futuresMonths?.items ?? [],
                19,
                (item) => set('futuresMonth', item?.value ?? ''),
                'futuresMonth',
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="FX" size="small" fullWidth value={fmtNumStr(form.fxRate)} onChange={(e) => setNum('fxRate', e.target.value)} inputProps={{ tabIndex: 20, inputMode: 'decimal' }} sx={numSx} />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderStaticAutocomplete(
                'Delivery Term',
                form.deliveryTerm,
                deliveryTerms?.items ?? [],
                21,
                (item) => set('deliveryTerm', item?.value ?? ''),
                'deliveryTerm',
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Warehouse" size="small" fullWidth value={form.warehouse} onChange={(e) => set('warehouse', e.target.value)} inputProps={{ tabIndex: 22 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={showShipment}
                    tabIndex={23}
                    onChange={(e) => {
                      const checked = e.target.checked
                      const cur = form.isShipDeli ?? 0
                      const deliOn = cur === 0 || cur === 2
                      // Checkbox CHỈ điều khiển hiển thị trên form print — KHÔNG
                      // xoá Shipment date. Shipment month luôn lưu cho traffic
                      // căn ngày ship (mọi HĐ Sales), dù print hiện Delivery.
                      set('isShipDeli', checked ? (deliOn ? 0 : 1) : (deliOn ? 2 : 3))
                    }}
                  />
                }
                label="Show Shipment Mo.?"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={showDelivery}
                    tabIndex={24}
                    onChange={(e) => {
                      const checked = e.target.checked
                      const cur = form.isShipDeli ?? 0
                      const shipOn = cur === 0 || cur === 1
                      // Untick → clear 2 ô Delivery date về null (empty string).
                      if (checked) {
                        set('isShipDeli', shipOn ? 0 : 2)
                      } else {
                        setForm((prev) => ({
                          ...prev,
                          isShipDeli: shipOn ? 1 : 3,
                          deliveryDateFrom: '',
                          deliveryDateTo: '',
                        }))
                      }
                    }}
                  />
                }
                label="Show Delivery Mo.?"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateTextField
                label="Shipment From"
                size="small"
                fullWidth
                value={form.shipmentDateFrom}
                onChange={(v) => set('shipmentDateFrom', v)}
                nativeInputProps={{ tabIndex: 25 }}
                error={!!fieldErrors.shipmentDateFrom}
                helperText={fieldErrors.shipmentDateFrom}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTextField
                label="Delivery From"
                size="small"
                fullWidth
                value={form.deliveryDateFrom}
                onChange={(v) => set('deliveryDateFrom', v)}
                disabled={!showDelivery}
                nativeInputProps={{ tabIndex: showDelivery ? 26 : -1 }}
                error={!!fieldErrors.deliveryDateFrom}
                helperText={fieldErrors.deliveryDateFrom}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateTextField
                label="Shipment To"
                size="small"
                fullWidth
                value={form.shipmentDateTo}
                onChange={(v) => set('shipmentDateTo', v)}
                nativeInputProps={{ tabIndex: 27 }}
                error={!!fieldErrors.shipmentDateTo}
                helperText={fieldErrors.shipmentDateTo}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTextField
                label="Delivery To"
                size="small"
                fullWidth
                value={form.deliveryDateTo}
                onChange={(v) => set('deliveryDateTo', v)}
                disabled={!showDelivery}
                nativeInputProps={{ tabIndex: showDelivery ? 28 : -1 }}
                error={!!fieldErrors.deliveryDateTo}
                helperText={fieldErrors.deliveryDateTo}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ minHeight: { xs: 4, sm: 10 } }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="PSS Terms" size="small" fullWidth value={form.pss} onChange={(e) => set('pss', e.target.value)} inputProps={{ tabIndex: 29 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderStaticAutocomplete(
                'Payment weight terms',
                form.paymentWeightTerm,
                paymentWeightTerms?.items ?? [],
                30,
                (item) => set('paymentWeightTerm', item?.value ?? ''),
                'paymentWeightTerm',
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Franchise" size="small" fullWidth value={fmtNumStr(form.franchise)} onChange={(e) => setNum('franchise', e.target.value)} inputProps={{ tabIndex: 31, inputMode: 'decimal' }} sx={numSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment terms"
                size="small"
                fullWidth
                multiline
                minRows={3}
                value={form.paymentTerms}
                onChange={(e) => set('paymentTerms', e.target.value)}
                inputProps={{ tabIndex: 32 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderStaticAutocomplete(
                'Conditions',
                form.conditionsCode,
                conditions?.items ?? [],
                33,
                (item) => set('conditionsCode', item?.value ?? ''),
                'conditionsCode',
              )}
            </Grid>
            <Grid item xs={12} sm={6} sx={{ alignSelf: 'stretch' }}>
              <Box sx={{ minHeight: { sm: 56 } }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Note"
                size="small"
                fullWidth
                multiline
                minRows={6}
                value={form.note}
                onChange={(e) => set('note', e.target.value)}
                inputProps={{ tabIndex: 34 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Freight"
                size="small"
                fullWidth
                value={fmtNumStr(form.freight)}
                onChange={(e) => setNum('freight', e.target.value)}
                inputProps={{ tabIndex: 35, inputMode: 'decimal' }}
                sx={numSx}
                error={!!fieldErrors.freight}
                helperText={fieldErrors.freight}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Logistics cost" size="small" fullWidth value={fmtNumStr(form.logisticsCost)} onChange={(e) => setNum('logisticsCost', e.target.value)} inputProps={{ tabIndex: 36, inputMode: 'decimal' }} sx={numSx} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Finance cost" size="small" fullWidth value={fmtNumStr(form.financeCost)} onChange={(e) => setNum('financeCost', e.target.value)} inputProps={{ tabIndex: 37, inputMode: 'decimal' }} sx={numSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Insurance cost" size="small" fullWidth value={fmtNumStr(form.insuranceCost)} onChange={(e) => setNum('insuranceCost', e.target.value)} inputProps={{ tabIndex: 38, inputMode: 'decimal' }} sx={numSx} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Bank charges" size="small" fullWidth value={fmtNumStr(form.bankCost)} onChange={(e) => setNum('bankCost', e.target.value)} inputProps={{ tabIndex: 39, inputMode: 'decimal' }} sx={numSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Weighing cost" size="small" fullWidth value={fmtNumStr(form.weightingCost)} onChange={(e) => setNum('weightingCost', e.target.value)} inputProps={{ tabIndex: 40, inputMode: 'decimal' }} sx={numSx} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                inputProps={{ tabIndex: 41 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
                <FormControlLabel
                  sx={{ ml: 0, mr: 0 }}
                  control={
                    <Checkbox size="small" checked={form.isIncludedRoyal} tabIndex={42} onChange={(e) => set('isIncludedRoyal', e.target.checked)} />
                  }
                  label="Royal Fee included ?"
                />
                <TextField
                  label="Royal Fee"
                  size="small"
                  sx={{ flex: '1 1 120px', minWidth: 100, ...numSx }}
                  value={fmtNumStr(form.royalFee)}
                  onChange={(e) => setNum('royalFee', e.target.value)}
                  inputProps={{ tabIndex: 43, inputMode: 'decimal' }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }} />

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Broker Code',
                form.brokerCode,
                form.brokerCode,
                brokerItems,
                brokersLoading,
                44,
                (q) => debouncedSearch((s) => fetchBrokers(s), q),
                (item) => {
                  set('brokerCode', item?.value ?? '')
                  const sn = item?.meta?.short_name as string | undefined
                  set('brokerFirm', (typeof sn === 'string' ? sn : '') || item?.label?.split('—')[1]?.trim() || '')
                },
                'brokerCode',
                true, // codeOnly — đã có field "Broker Name" readonly riêng
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Broker Name" size="small" fullWidth value={form.brokerFirm} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 45 }} sx={readonlySx} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Broker's Ref" size="small" fullWidth value={form.brokerRef} onChange={(e) => set('brokerRef', e.target.value)} inputProps={{ tabIndex: 46 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Commission (USD/MT)"
                size="small"
                fullWidth
                value={fmtNumStr(form.commission)}
                onChange={(e) => setNum('commission', e.target.value)}
                inputProps={{ tabIndex: 47, inputMode: 'decimal' }}
                sx={numSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderAutocomplete(
                'Trader In Charge',
                form.traderCode,
                form.traderName,
                traderItems,
                tradersLoading,
                48,
                (q) => debouncedSearch((s) => fetchTraders(s), q),
                (item) => {
                  set('traderCode', item?.value ?? '')
                  set('traderName', item?.label ?? '')
                },
                'traderCode',
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="PIC" size="small" fullWidth value={form.traderCode} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 49 }} sx={readonlySx} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Consignee Code" size="small" fullWidth value={form.consigneeCode} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 50 }} sx={readonlySx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Packaging Code" size="small" fullWidth value={form.packagingCode} InputProps={{ readOnly: true }} inputProps={{ tabIndex: 51 }} sx={readonlySx} />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={form.sentContract} onChange={(e) => set('sentContract', e.target.checked)} tabIndex={52} />}
                  label="Sent contract"
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={form.receivedContract} onChange={(e) => set('receivedContract', e.target.checked)} tabIndex={53} />}
                  label="Received contract"
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={form.sentPaper} onChange={(e) => set('sentPaper', e.target.checked)} tabIndex={54} />}
                  label="Sent paper"
                />
                <FormControlLabel
                  control={<Checkbox size="small" checked={form.receivedPaper} onChange={(e) => set('receivedPaper', e.target.checked)} tabIndex={55} />}
                  label="Received paper"
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
